import React, { useMemo } from 'react';
import { getImageSrcAndSrcSet } from '../services/getUrlFromCdn';
import ImageSlider from './ImageSlider';
import clsx from 'clsx';
import useCheckoutState, { checkoutLayouts } from '../hooks/useCheckoutState';
import { checkoutTypes } from '../enums/checkout-types';

const UnsplashAttribution = ({ photographer, photographerUrl, logoClearancePx }) => {
  if (!photographer) return null;

  const handle = photographerUrl?.match(/\/@([^/?#]+)/)?.[1];

  return (
    <a
      href={photographerUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="cp_image-attribution absolute bottom-2 right-2 flex whitespace-nowrap rounded-[min(var(--cp_checkout-form-fields-input-border-radius,4px),6px)] bg-black/50 px-1.5 py-0.5 text-[10px] leading-4 text-white/90 hover:text-white"
      style={{
        // A logo bubble sits centered on the image's bottom edge; stop the
        // credit short of it so it stays visible at narrow widths.
        maxWidth: logoClearancePx
          ? `calc(50% - ${logoClearancePx / 2 + 12}px)`
          : 'calc(100% - 1rem)',
      }}
    >
      <span className="truncate">{handle ? `@${handle}` : photographer}</span>
      <span className="shrink-0">&nbsp;on Unsplash</span>
    </a>
  );
};

const Media = React.memo(
  ({ media, logoClearancePx }) => {
    const hasMedia = media && media.length > 0;
    const { checkoutLayout, isPreview, checkoutType } = useCheckoutState();

    const containerMaxWidth = useMemo(() => {
      switch (checkoutLayout) {
        case checkoutLayouts.DOUBLE:
        case checkoutLayouts.SPLIT:
          return 448;
        default:
          return 480;
      }
    }, [checkoutLayout]);

    const mediaSizesCalculated = useMemo(
      () =>
        media.map(m => {
          // Now the image object has the file data loaded
          const maxWidth = parseInt(m.width > containerMaxWidth ? containerMaxWidth : m.width);

          // Calculate the aspect ratio and the resulting height
          const aspectRatio = m.width / m.height;
          const newHeight = parseInt(maxWidth / aspectRatio);

          const image = getImageSrcAndSrcSet({
            url: m.url,
            width: maxWidth,
            height: newHeight,
            isPreview,
          });

          return {
            ...m,
            width: maxWidth,
            height: newHeight,
            src: image.src,
            srcSet: image.srcSet,
            aspectRatio,
          };
        }),
      [media, containerMaxWidth, isPreview],
    );

    const imageStyle = {
      maxWidth: '100%',
      width: '100%',
      height: 'auto',
    };

    if (!hasMedia) {
      return null;
    }

    if (mediaSizesCalculated.length === 1) {
      return (
        <div className="relative">
          <img
            src={mediaSizesCalculated[0].src}
            srcSet={mediaSizesCalculated[0].srcSet}
            alt={media[0].name}
            width={mediaSizesCalculated[0].width}
            height={mediaSizesCalculated[0].height}
            className={clsx('cp_product-image object-cover object-center color-transparent', {
              'rounded-[var(--cp_checkout-border-radius)]':
                checkoutLayout === checkoutLayouts.DOUBLE ||
                checkoutLayout === checkoutLayouts.SPLIT,
            })}
            style={{
              ...(checkoutLayout === checkoutLayouts.CENTER && checkoutType !== checkoutTypes.EMBED
                ? {
                    borderTopLeftRadius:
                      'max(0px, calc(var(--cp_checkout-border-radius) - var(--cp_checkout-border-width, 0px)))',
                    borderTopRightRadius:
                      'max(0px, calc(var(--cp_checkout-border-radius) - var(--cp_checkout-border-width, 0px)))',
                  }
                : {}),
              ...imageStyle,
            }}
            loading="eager"
          />
          <UnsplashAttribution
            photographer={media[0].photographer}
            photographerUrl={media[0].photographerUrl}
            logoClearancePx={logoClearancePx}
          />
        </div>
      );
    }

    const clipStyle = (() => {
      if (checkoutLayout === checkoutLayouts.DOUBLE || checkoutLayout === checkoutLayouts.SPLIT) {
        return { borderRadius: 'var(--cp_checkout-border-radius)' };
      }
      if (checkoutLayout === checkoutLayouts.CENTER && checkoutType !== checkoutTypes.EMBED) {
        const r =
          'max(0px, calc(var(--cp_checkout-border-radius) - var(--cp_checkout-border-width, 0px)))';
        return { borderTopLeftRadius: r, borderTopRightRadius: r };
      }
      return undefined;
    })();

    return (
      <ImageSlider.Carousel
        count={mediaSizesCalculated.length}
        clipStyle={clipStyle}
        aspectRatio={`${mediaSizesCalculated[0].width} / ${mediaSizesCalculated[0].height}`}
      >
        {mediaSizesCalculated.map((mediaItem, index) => (
          <ImageSlider.Slide key={mediaItem._id}>
            <div className="relative">
              <img
                src={mediaItem.src}
                srcSet={mediaItem.srcSet}
                alt={mediaItem.title}
                width={mediaItem.width}
                height={mediaItem.height}
                className="cp_product-image object-cover object-center color-transparent"
                style={imageStyle}
                loading={index === 0 ? 'eager' : 'lazy'}
              />
              <UnsplashAttribution
                photographer={mediaItem.photographer}
                photographerUrl={mediaItem.photographerUrl}
                logoClearancePx={logoClearancePx}
              />
            </div>
          </ImageSlider.Slide>
        ))}
      </ImageSlider.Carousel>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.media === nextProps.media && prevProps.checkoutType === nextProps.checkoutType;
  },
);

export default Media;
